import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ChartTypeIcon from "../ChartTypeIcon";
import ChartOptionPanels from "../ChartOptionPanels";
import ChartOrientationField from "../ChartOptionPanels/ChartOrientationField";
import { AppFormField, AppFormGrid } from "../layout";
import { AppAutocomplete, AppTextField } from "../form";
import { GRID_HALF, chartOptionInputSx } from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import {
  applyChartTypeChange,
  applyOrientationChange,
  type WidgetChartMapping,
} from "../../utils/chartMappingBuilder";
import {
  getChartTypeDef,
  getSwitchableChartTypes,
  WIDGET_CHART_TYPES,
} from "../../utils/chartTypeRegistry";

type ChartTypeOption = {
  id: string;
  label: string;
};

type Props = {
  mapping: WidgetChartMapping;
  onChange: (next: WidgetChartMapping) => void;
  disabled?: boolean;
};

export default function ChartEditorPanel({ mapping, onChange, disabled }: Props) {
  const { i18n, t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.chartEditorPanel;
  const isTr = i18n.language?.startsWith("tr");
  const def = getChartTypeDef(mapping.chartType);
  const switchable = getSwitchableChartTypes(mapping.chartType);

  const labelFor = (id: string) => {
    const d = WIDGET_CHART_TYPES.find((c) => c.id === id);
    if (!d) return id;
    return isTr ? d.labelTr : d.labelEn;
  };

  const chartTypeOptions: ChartTypeOption[] = useMemo(
    () => switchable.map((c) => ({ id: c.id, label: labelFor(c.id) })),
    [switchable, isTr]
  );

  const selectedChartType =
    chartTypeOptions.find((o) => o.id === mapping.chartType) ?? chartTypeOptions[0];

  return (
    <Box>
      <AppFormGrid alignItems="stretch">
        <AppFormField size={def?.supportsOrientation ? GRID_HALF : { xs: 12 }}>
          <AppAutocomplete<ChartTypeOption>
            label={t("chartEditor.chartType")}
            options={chartTypeOptions}
            value={selectedChartType}
            disabled={disabled}
            onChange={(_, opt) => opt && onChange(applyChartTypeChange(mapping, opt.id))}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable
            sx={chartOptionInputSx}
            renderOption={(props, option) => {
              const { key, ...rest } = props as typeof props & { key: string };
              return (
                <ListItem key={key} {...rest} sx={styles.listItem}>
                  <ListItemIcon sx={styles.listIcon}>
                    <ChartTypeIcon typeId={option.id} size={26} />
                  </ListItemIcon>
                  <ListItemText primary={option.label} primaryTypographyProps={{ fontWeight: 500 }} />
                </ListItem>
              );
            }}
            renderInput={(params) => (
              <AppTextField
                {...params}
                label={t("chartEditor.chartType")}
                sx={chartOptionInputSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedChartType ? (
                    <>
                      <Box sx={styles.startAdornmentBox}>
                        <ChartTypeIcon typeId={selectedChartType.id} size={22} />
                      </Box>
                      {params.InputProps.startAdornment}
                    </>
                  ) : (
                    params.InputProps.startAdornment
                  ),
                }}
              />
            )}
          />
        </AppFormField>

        {def?.supportsOrientation && (
          <AppFormField size={GRID_HALF}>
            <ChartOrientationField
              value={mapping.orientation || "vertical"}
              disabled={disabled}
              onChange={(orientation) => onChange(applyOrientationChange(mapping, orientation))}
            />
          </AppFormField>
        )}
      </AppFormGrid>

      <Typography variant="caption" color="text.secondary" sx={styles.familyHint}>
        {t("chartEditor.familyHint", { family: def?.family || "cartesian" })}
      </Typography>

      <ChartOptionPanels mapping={mapping} onChange={onChange} disabled={disabled} />
    </Box>
  );
}
